import React, { FC, useEffect } from "react";

import { useInsuranceFilters } from "../Utils/Context/filtersContext";
import { useInsuranceLabels } from "../Utils/Context/labelsContext";

import { Combobox } from "../Partials/Combobox";
import { PartialsBackgroundVariants } from "../Partials/PartialsBackgroundVariants";
import { filterServices } from "../Utils/Filters/filterServices";
import { UrlHandler } from "../Utils/UrlHandler";
import { PracticalInformationModal } from "./PracticalInformationModal";
import { InsuranceServiceDto } from "@apiModels/Insurance/InsuranceServiceDto.csharp";

interface ServicesComboboxProps {
  services: InsuranceServiceDto[];
  getDataFromUrl?: boolean;
  variant?: PartialsBackgroundVariants;
  displayPracticalInformation?: boolean;
}

export const ServicesCombobox: FC<ServicesComboboxProps> = (props) => {
  const {
    services,
    variant,
    getDataFromUrl = true,
    displayPracticalInformation = false,
  } = props;

  const { service, locations, specialization, setService } =
    useInsuranceFilters();

  const { inputs, headings } = useInsuranceLabels();

  const filteredServices = filterServices(
    {
      specialization,
      locations,
    },
    services
  );

  useEffect(() => {
    if (getDataFromUrl) {
      const foundService = UrlHandler.getEntryFromUrlQuery<InsuranceServiceDto>(
        services,
        "serviceId"
      );
      foundService && setService(foundService as InsuranceServiceDto);
    }
  }, []);

  useEffect(() => {
    if (filteredServices && filteredServices.length == 1) {
      const [singleService] = filteredServices;
      selectionChange(singleService);
    }
  }, [specialization]);

  const inputChange = (service: InsuranceServiceDto, inputValue?: string) => {
    if (!inputValue) return true;
    return service.bookingName
      .toLowerCase()
      .startsWith(inputValue.toLowerCase());
  };

  const selectionChange = (service: InsuranceServiceDto | null) => {
    if (!service) {
      setService(null);
    }

    setService(service);
  };

  const renderService = (service: InsuranceServiceDto) => (
    <span className="specializationCombobox__item">{service.bookingName}</span>
  );

  const serviceToString = (service: InsuranceServiceDto | null) =>
    service?.bookingName ?? "";

  const showPracticalInfoButton =
    displayPracticalInformation && service?.practicalInfoInsurance;

  return (
    <div className="serviceCombobox">
      <Combobox<InsuranceServiceDto>
        label={inputs.servicesLabel}
        placeholder={inputs.servicesPlaceholder}
        items={filteredServices}
        currentItem={service}
        onSelectionChange={selectionChange}
        onInputValueChange={inputChange}
        renderItemInList={renderService}
        itemToString={serviceToString}
        className="serviceCombobox__select"
        backgroundVariant={variant}
        disabled={filteredServices.length == 0}
      />
      {showPracticalInfoButton && (
        <PracticalInformationModal
          className="serviceCombobox__practicalInfo"
          label={headings.practicalInfoHeading}
          body={service.practicalInfoInsurance}
        />
      )}
    </div>
  );
};
